import React, { ChangeEvent, useEffect, useState } from 'react';
import { menuClasses, SubMenu } from 'react-pro-sidebar';
import styled from 'styled-components';
import {
  Label,
  Panel,
  TextInput,
  Button,
  Card,
  TextArea,
} from '@uhg-harmony/uitk-react';
import ListItemIcon from '@mui/material/ListItemIcon';
import { Arrow, StyledPanelTitle } from '../SettingsPanel/SettingsPanel';
import { getWidget } from 'shared/widgets';
import Tooltip from 'shared/components/Tooltip';
import uuidv4 from 'uuid/v4';
import {
  DEFAULT_CUSTOM_WIDGET_MESSAGE,
  HEADER_PLACEHOLDER_HEIGHT,
  HEADER_SECTION_ID,
  RepeaterWidgetInitialData,
  widgetTypes,
} from 'builder/util/constants';
import { getInitialChildren } from 'builder/util/getInitialChildren';
import config from 'builder/config';
import { getLeftTopPosition } from '../SiteBuilderHelper';
import {
  IconBolt,
  IconLetterCase,
  IconPhoto,
  IconLayersLinked,
  IconLink,
  IconSeparator,
  IconCode,
  IconSection,
  IconPhotoPlus,
  IconListDetails,
  IconApps,
  IconCards,
  IconSlideshow,
  IconFlagSpark,
  IconNotebook,
  IconMap2,
  IconCalendarMonth,
  IconTextCaption,
  IconForms,
  IconCheckbox,
  IconGradienter,
  IconSelect,
  IconFileUpload,
  IconFileTextSpark,
  IconVideo,
  IconBell,
  IconSearch,
} from '@tabler/icons-react';
import {
  StyledCard,
  StyledNewPage,
  StyledPage,
  StyledSettingName,
  StyledSettingsCard,
  StyledTextName,
  StyledTitle,
  iconStyle,
} from '../PagesPanel/PagesPanel';
import { Popover } from '@material-ui/core';
import { useDispatch } from 'react-redux';
import {
  addChild as addNewChild,
  updateWidgetConfig,
} from 'shared/state/ducks/sites';
import { saveOrUpdateContentToHorizon } from 'shared/api/horizonContentApi';
import { syncAddAcrossRepeaterContainers } from 'shared/widgets/RepeaterWidget/RepeaterUtils';
import { defaultTheme } from 'shared/theme/theme';
// Alias shared Icon to avoid collisions
import { Icon as SharedIcon } from 'shared/theme/link-ui-react/Icon';

const { Group } = Panel;

const StyledMenuItem = styled(SubMenu)<{ layoutLocked?: boolean }>`
  margin-top: 16px;
  ${props =>
    props.layoutLocked &&
    `
      pointer-events: none;
      cursor: not-allowed;
      opacity: 0.5;
      background-color: #f0f0f0;
  `}
`;

StyledMenuItem.displayName = 'submenu';

const StyledPanel = styled.div`
  & button[class^='Panelcomponent__HeaderSection'] {
    height: 2.5em;
    background: rgba(141, 145, 160, 0.07);
    border-radius: 0;
    &:hover,
    &:focus {
      box-shadow: none;
    }
  }
  & span[class^='Panelcomponent__HeaderMainContent'] {
    height: 2.5em;
  }
  & div[class^='uitk-panel Panelcomponent__Panel'] {
    margin-bottom: 0;
    border: none;
  }
  & div[class^='Panelcomponent__Content'] {
    padding: 0;
  }
`;

const StyledHeading = styled.p`
  color: ${defaultTheme.colors.aux.black};
  font-weight: 900;
  font-size: 21px;
  font-family: OptumSans, sans-serif;
`;

const TopSection = styled.div`
  display: flex;
  align-items: center;
  padding: 1.125rem 0 0.5rem 1.25rem;
`;

TopSection.displayName = 'topsection';

const IconGroupWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
`;

const IconWrapper = styled.div`
  padding: 1em 0;
  cursor: pointer;
  border-width: 0.5px 1px 0.5px 0;
  border-style: solid;
  border-color: ${defaultTheme.colors.aux.lightGrey};
  font-family: OptumSans, sans-serif;
  font-size: 0.9rem;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

export const StyledPopover = styled(Popover)`
  margin-top: -14px;
  .MuiPaper-root {
    overflow-x: unset;
    overflow-y: unset;
    left: 350px !important;
    box-shadow: 1px 1px 5px -2px grey;
  }
`;

export const StyledTextInput = styled(TextInput)`
  font-family: OptumSans, sans-serif;
  padding: 0.5rem;
  font-size: 14px;
  display: flex;
`;

const ButtonWrapper = styled.div`
  display: flex;
  .uitk-button {
    margin-right: 7px;
    margin-top: 50px;
    font-family: OptumSans, sans-serif;
    height: 10px;
  }
`;

const StyledWidgetMessage = styled.div`
  font-family: OptumSans, sans-serif;
  font-size: 12px;
  padding: 1.5rem;
  font-style: italic;
`;

const CustomWidgetPanelDiv = styled.div`
  width: 250px;
  & > label + div + div {
    font-weight: 400;
    :hover {
      font-weight: 400;
    }
  }
`;

interface createChildWidgetsOptionalParams {
  isBannerWidgetChild?: boolean;
  parentWidgetType?: string;
  visibility?: boolean;
  parentConfig?: any;
}
export interface WidgetsPanelProps<T> {
  site: Site;
  pages: Array<Page>;
  updateSite: (site: Site) => void;
  getLocationData?: any;
  currentPage?: PageVersion;
  setResetToFalse: () => void;
  addChild?: (
    type: string,
    initialConfig: T,
    location: number,
    widgetId: string,
    newParentWidgetId: string,
    tabIndex: number
  ) => void;
  updateWidgetPosition?: (
    bannerWidgetId: string,
    height: number,
    oldHeight: number
  ) => void;
  widgetState: Widget<T>;
  onUpdateConfig: (config: any) => void;
  removeWidget?: (widgetId: string) => void;
  isEditingTemplate?: boolean;
}

export const WidgetsPanel: React.FC<WidgetsPanelProps<any>> = (
  props: WidgetsPanelProps<any>
) => {
  const {
    addChild,
    currentPage,
    isEditingTemplate,
    setResetToFalse,
    site,
  } = props;
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [locData, setLocData] = useState(undefined);
  const [isSettingsOpen, setSettingsOpen] = useState(false);
  const [settingsAnchorEl, setSettingsAnchorEl] = useState<SVGElement | null>(
    null
  );
  const [openEditWidget, setOpenEditWidgetFlag] = useState(false);
  const [activeId, setActiveId] = useState('');
  const [widgetNames, setWidgetNames] = useState([]);
  const [parentWidgets, setParentWidgets] = useState([]);
  const [updatedCustomWidgets, setUpdatedCustomWidgets] = useState([]);
  const widgetOpen = Boolean(settingsAnchorEl);
  const widgetsId = widgetOpen ? 'widget-popover' : undefined;
  const dispatch = useDispatch();

  const layoutLocked = !isEditingTemplate && currentPage?.layoutLocked;

  const headerHeight =
    props.currentPage?.content[HEADER_SECTION_ID]?.config.height ||
    HEADER_PLACEHOLDER_HEIGHT;

  const reset = () => {
    setSettingsAnchorEl(null);
    setSettingsOpen(false);
    setOpenEditWidgetFlag(false);
  };

  const toggleWidgetsPanelOpen = (isOpen: boolean) => {
    if (layoutLocked && isOpen) return;
    setIsPanelOpen(isOpen);
  };

  const handleSettingsClick = (e, item) => {
    e.stopPropagation();
    e.preventDefault();
    setSettingsOpen(!isSettingsOpen);
    setSettingsAnchorEl(e.currentTarget);
    setActiveId(item);
  };

  const handleInputClick = (e: React.MouseEvent<any>) => {
    e.stopPropagation();
  };

  const handleClose = (e: React.MouseEvent<any>) => {
    e.stopPropagation();
    e.preventDefault();
    reset();
  };

  const handleDeleteClick = widgetId => {
    const widgetToDelete = props.site.customWidgets.find(
      widget => widget.widgetId === widgetId
    );
    const childWidgets = widgetToDelete?.widgetConfig.children || [];

    let updatedCustomWidgets = props.site.customWidgets.filter(
      widget => widget.widgetId !== widgetId
    );

    if (childWidgets.length > 0) {
      childWidgets.forEach(
        childId =>
          (updatedCustomWidgets = updatedCustomWidgets.filter(
            widget => widget.widgetId !== childId
          ))
      );
    }
    reset();
    dispatch(
      props.updateSite({
        ...props.site,
        customWidgets: updatedCustomWidgets,
      })
    );
  };

  const handleItemNameChange = (e: ChangeEvent<HTMLInputElement>, index) => {
    const updatedItems = widgetNames.map(item =>
      item.id === index ? { ...item, name: e.target.value } : item
    );

    setWidgetNames(updatedItems);

    const updatedWidgets = props.site.customWidgets.map(widget =>
      widget.widgetId === index
        ? { ...widget, widgetName: e.target.value }
        : widget
    );

    setUpdatedCustomWidgets(updatedWidgets);
  };

  const handleItemDescriptionChange = (
    e: ChangeEvent<HTMLInputElement>,
    index
  ) => {
    const updatedItems = widgetNames.map(item =>
      item.id === index ? { ...item, description: e.target.value } : item
    );

    setWidgetNames(updatedItems);

    // Update the widget name in the local state
    const updatedWidgets = props.site.customWidgets.map(widget =>
      widget.widgetId === index
        ? { ...widget, widgetDescription: e.target.value }
        : widget
    );

    setUpdatedCustomWidgets(updatedWidgets);
  };

  const handleSaveCustomWidgets = () => {
    reset();
    dispatch(
      props.updateSite({
        ...props.site,
        customWidgets: updatedCustomWidgets,
      })
    );
  };

  const complexWidgetTypes = [
    widgetTypes.Section,
    widgetTypes.Card,
    widgetTypes.AccordionWidget,
    widgetTypes.Tabs,
    widgetTypes.Form,
    widgetTypes.Banner,
    widgetTypes.Slideshow,
    widgetTypes.Slideshow,
  ];

  /**
   * Finds a visible repeater Section container whose bounding box contains the
   * given coordinates. Used to auto-adopt a dropped widget into the repeater.
   * @param x - drop x coordinate (relative to editing container)
   * @param y - drop y coordinate (relative to editing container)
   * @param page - current page version
   * @returns the Section widget ID, or null
   */
  function findRepeaterContainerAtPosition(
    x: number,
    y: number,
    page: PageVersion
  ): string | null {
    const content = page.content;
    for (const id of Object.keys(content)) {
      const w = content[id];
      if (
        w?.type === widgetTypes.Section &&
        w.config?.visible !== false &&
        w.config?.parentId &&
        content[w.config.parentId]?.type === widgetTypes.RepeaterWidget
      ) {
        const { xLocation, yLocation, width, height } = w.config;
        if (
          x >= xLocation &&
          x <= xLocation + width &&
          y >= yLocation &&
          y <= yLocation + height
        ) {
          return id;
        }
      }
    }
    return null;
  }

  useEffect(() => {
    props.getLocationData(locData);
  }, [locData]);

  useEffect(() => {
    if (!props.site?.customWidgets) {
      return;
    }

    const complexWidgetChildren = props.site.customWidgets
      .filter(widget => complexWidgetTypes.includes(widget.widgetConfig.type))
      .flatMap(widget => widget.widgetConfig.children || []);

    const parentWidgets = props.site.customWidgets
      .filter(widget => !complexWidgetChildren.includes(widget.widgetId))
      .map(widget => widget.widgetId);

    setParentWidgets(parentWidgets);
  }, [props.site?.customWidgets]);

  useEffect(() => {
    const updatedWidgetNames = parentWidgets.map(widgetId => ({
      id: widgetId,
      name: props.site.customWidgets.find(
        widget => widget.widgetId === widgetId
      )?.widgetName,
      description: props.site.customWidgets.find(
        widget => widget.widgetId === widgetId
      )?.widgetDescription,
    }));

    setWidgetNames(prevWidgetNames => [
      ...prevWidgetNames,
      ...updatedWidgetNames,
    ]);
  }, [parentWidgets]);

  function createChildWidgets(
    parentWidgetId,
    childConfigs,
    e,
    optionalParams?: createChildWidgetsOptionalParams
  ) {
    const { isBannerWidgetChild = false, visibility = true } =
      optionalParams || {};
    const elementLeftPosition = getLeftTopPosition('editingContainerId')[0];
    const elementTopPosition = getLeftTopPosition('editingContainerId')[1];
    if (optionalParams?.parentWidgetType === widgetTypes.RepeaterWidget) {
      const sectionWidgetIds = [];
      childConfigs.map((childWidget, index) => {
        const widget = getWidget(childWidget.widgetType, true);
        const yLocOffset = e.clientY - elementTopPosition;
        const yLoc = yLocOffset > 0 ? yLocOffset : 0;
        let defaultChildConfig = {
          ...widget.initialConfig,
          visible: visibility,
          xLocation: e.clientX + childWidget.x - elementLeftPosition,
          yLocation: yLoc + childWidget.y,
          width: childWidget.width,
          height: childWidget.height,
          minWidth: childWidget.minWidth,
          minHeight: childWidget.minHeight,
          parentId: parentWidgetId,
          repeaterWidgetIndex: childWidget.repeaterWidgetIndex,
        };

        const widgetId = uuidv4();
        addChild(
          childWidget.widgetType,
          defaultChildConfig,
          0,
          widgetId,
          parentWidgetId,
          childWidget.tabIndex
        );
        setLocData({
          x: e.clientX + childWidget.x - elementLeftPosition,
          y: yLoc + childWidget.y,
          widgetType: childWidget.widgetType,
        });

        sectionWidgetIds.push(widgetId);

        const jsonString = JSON.stringify(RepeaterWidgetInitialData);
        const siteId = props.site.siteId;
        const pageId = props.currentPage.pageId;

        saveOrUpdateContentToHorizon(
          siteId,
          pageId,
          parentWidgetId,
          jsonString
        ).catch(error => {
          console.error('Error saving content:', error);
        });

        const children = getInitialChildren('RepeaterSectionChildren');
        createChildWidgets(widgetId, children[index], e);
      });

      const repeaterDataMapping = {};
      sectionWidgetIds.forEach((containerId, index) => {
        repeaterDataMapping[containerId] = index;
      });

      dispatch(
        updateWidgetConfig(
          site.siteId,
          currentPage.pageId,
          parentWidgetId,
          {
            ...optionalParams?.parentConfig,
            repeaterDataMapping,
          },
          false
        )
      );
    } else {
      childConfigs.forEach(childWidget => {
        const widget = getWidget(childWidget.widgetType, true);
        const yLocOffset = e.clientY - elementTopPosition;
        const yLoc = yLocOffset > 0 ? yLocOffset : 0;
        let defaultConfig = {
          ...widget.initialConfig,
          visible: visibility,
          xLocation: e.clientX + childWidget.x - elementLeftPosition,
          yLocation: yLoc + childWidget.y,
          originalYLocation: yLoc + childWidget.y,
          width: childWidget.width,
          height: childWidget.height,
          minWidth: childWidget.minWidth,
          minHeight: childWidget.minHeight,
          parentId: parentWidgetId,
          font: site.theme.font.family.base.value,
        };
        if (childWidget.hasOwnProperty('repeaterWidgetKey')) {
          defaultConfig = {
            ...defaultConfig,
            repeaterWidgetKey: childWidget.repeaterWidgetKey,
            repeaterWidgetId: childWidget.repeaterWidgetId,
          };
        }
        if (isBannerWidgetChild) {
          const bannerchildYLoc = headerHeight
            ? headerHeight + childWidget.y
            : 0 + childWidget.y;
          defaultConfig = {
            ...widget.initialConfig,
            xLocation: childWidget.x,
            yLocation: bannerchildYLoc,
            originalYLocation: bannerchildYLoc,
            width: childWidget.width,
            height: childWidget.height,
            minWidth: childWidget.minWidth,
            minHeight: childWidget.minHeight,
            parentId: parentWidgetId,
          };
        }
        let widgetConfig;
        switch (childWidget.widgetType) {
          case widgetTypes.Text:
            widgetConfig = {
              ...defaultConfig,
              text: childWidget.text,
              bold: childWidget.bold,
              align: childWidget.align,
              fontSize: childWidget.fontSize,
              backgroundData: childWidget?.backgroundData,
              textColor: childWidget?.textColor,
              lineSpacing: childWidget.lineSpacing,
            };
            break;
          case widgetTypes.Image:
            widgetConfig = {
              ...defaultConfig,
              backgroundImage: childWidget.backgroundImage,
            };
            break;
          case widgetTypes.Button:
            widgetConfig = {
              ...defaultConfig,
              displayText: childWidget.displayText,
              bold: childWidget.bold,
              align: childWidget.align,
              paddingData: childWidget.paddingData,
              backgroundData: childWidget.backgroundData,
              textColor: childWidget.textColor,
              borderData: childWidget.borderData,
              lineColor: childWidget.lineColor,
              action: childWidget.action,
              site: childWidget.site ?? site,
              hoverData: childWidget.hoverData,
            };
            break;
          case widgetTypes.TextInput:
            widgetConfig = {
              ...defaultConfig,
              label: childWidget.label,
              required: childWidget.required,
            };
            break;
          case widgetTypes.RadioButton:
            widgetConfig = {
              ...defaultConfig,
              label: childWidget.label,
              value: childWidget.value,
              required: childWidget.required,
            };
            break;
          case widgetTypes.Divider:
            widgetConfig = {
              ...defaultConfig,
              text: childWidget.text,
              bold: childWidget.bold,
              align: childWidget.align,
              lineColor: childWidget.lineColor,
              direction: childWidget.direction,
              thickness: childWidget.thickness,
            };
            break;
          case widgetTypes.LoginButton:
          case widgetTypes.RichText:
            widgetConfig = {
              ...defaultConfig,
            };
            break;
          // using uniqueId for widgets that need to be created with nested children
          case widgetTypes.Card:
            widgetConfig = {
              ...defaultConfig,
              uniqueId: childWidget.uniqueId,
            };
            break;
          case widgetTypes.Link:
            widgetConfig = {
              ...defaultConfig,
              displayText: childWidget.displayText,
              url: childWidget.url,
              iconColor: childWidget.iconColor,
              newTab: childWidget.newTab,
              bold: childWidget.bold,
              underline: childWidget.underline,
              fontSize: childWidget.fontSize,
              textColor: childWidget.textColor,
              backgroundData: childWidget.backgroundData,
              hoverData: childWidget.hoverData,
            };
            break;
          case widgetTypes.Section: {
            widgetConfig = {
              ...defaultConfig,
              backgroundData: childWidget.backgroundData,
              backgroundImage: childWidget.backgroundImage,
              borderData: childWidget.borderData,
              boxShadowData: childWidget.boxShadowData,
            };
            break;
          }
          case widgetTypes.Tabs: {
            widgetConfig = {
              ...defaultConfig,
              tabs: childWidget.tabs,
              currentTabIndex: childWidget.currentTabIndex,
            };
            break;
          }
          default:
            widgetConfig = { ...defaultConfig };
            break;
        }
        const widgetId = uuidv4();
        addChild(
          childWidget.widgetType,
          widgetConfig,
          0,
          widgetId,
          parentWidgetId,
          childWidget.tabIndex
        );
        if (childWidget.uniqueId) {
          const grandchildren = getInitialChildren(childWidget.uniqueId);
          createChildWidgets(widgetId, grandchildren, e, {
            isBannerWidgetChild: false,
            visibility: childWidget.visible,
          });
        }
        setLocData({
          x: e.clientX + childWidget.x - elementLeftPosition,
          y: yLoc + childWidget.y,
          originalYLocation: yLoc + childWidget.y,
          widgetType: childWidget.widgetType,
        });
      });
    }
  }

  // Add and update widget config when dropped
  const onDropWidget = (e, widgetType, widgetState = null) => {
    const eleLeft = getLeftTopPosition('editingContainerId')[0];
    const eleTop = getLeftTopPosition('editingContainerId')[1];
    const xLoc = e.clientX - eleLeft;
    const yLocOrig = e.clientY - eleTop;
    const yLoc = yLocOrig > 0 ? yLocOrig : 0;
    e.preventDefault();
    setResetToFalse();
    if (widgetState?.config?.isCustomWidget === true) {
      const widgetId = uuidv4();
      const newWidgetConfig = {
        ...widgetState.config,
        xLocation: xLoc,
        yLocation: yLoc,
        isCustomWidget: false,
        font: site.theme.font.family.base.value,
        originalYLocation: yLoc,
      };
      addChild(widgetType, newWidgetConfig, 0, widgetId, 'root', -1);
      setLocData({
        x: xLoc,
        y: yLoc,
        originalYLocation: yLoc,
        widgetType: widgetType,
      });
      if (complexWidgetTypes.includes(widgetType)) {
        const childWidgetIds = [];

        widgetState.children.map(child => {
          const childWidgetId = uuidv4();
          childWidgetIds.push(childWidgetId);

          const childWidget = props.site.customWidgets.find(
            widget => widget.widgetId === child
          )?.widgetConfig;

          const childXLoc =
            xLoc +
            (childWidget.config.xLocation - widgetState.config.xLocation);
          const childYLoc =
            yLoc +
            (childWidget.config.yLocation - widgetState.config.yLocation);

          const customChildWidget = {
            ...childWidget,
            config: {
              ...childWidget.config,
              xLocation: childXLoc,
              yLocation: childYLoc,
              originalYLocation: childYLoc,
              isCustomWidget: false,
            },
          };

          dispatch(
            addNewChild(
              site.siteId,
              props.currentPage.pageId,
              widgetId,
              customChildWidget.type,
              customChildWidget.config,
              0,
              childWidgetId,
              customChildWidget.tabIndex
            )
          );
        });
        newWidgetConfig.children = childWidgetIds;
      }
    } else {
      const widget = getWidget(widgetType, true);
      // Create Parent widget
      const newParentWidgetId = uuidv4();
      let children = [];
      let widgetConfig = {
        ...widget.initialConfig,
        xLocation: xLoc,
        yLocation: yLoc,
        font: site.theme.font.family.base.value,
        originalYLocation: yLoc,
      };
      if (widgetType === widgetTypes.Banner) {
        widgetConfig = {
          ...widget.initialConfig,
          xLocation: 0,
          yLocation: headerHeight ?? HEADER_PLACEHOLDER_HEIGHT,
        };
      }
      if (widgetType === widgetTypes.Application) {
        widgetConfig = {
          ...widgetConfig,
          uniqueId: newParentWidgetId,
          siteId: site.siteId,
          pageId: props.currentPage.pageId,
        };
      }

      // Auto-detect if dropping a simple widget into a repeater container
      const isSimpleWidget =
        !complexWidgetTypes.includes(widgetType) &&
        widgetType !== widgetTypes.Navigation &&
        widgetType !== widgetTypes.RepeaterWidget;

      if (isSimpleWidget && currentPage) {
        const repeaterSectionId = findRepeaterContainerAtPosition(
          xLoc,
          yLoc,
          currentPage
        );
        if (repeaterSectionId) {
          addChild(
            widgetType,
            widgetConfig,
            0,
            newParentWidgetId,
            repeaterSectionId,
            -1
          );
          setLocData({
            x: xLoc,
            y: yLoc,
            originalYLocation: yLoc,
            widgetType: widgetType,
          });

          // Build a snapshot of the page state that includes the newly added widget
          // so the sync function can read sibling containers and compute metadata.
          const addedWidget = {
            id: newParentWidgetId,
            type: widgetType,
            config: {
              visible: true,
              ...widgetConfig,
              parentId: repeaterSectionId,
            },
            children: [],
            tabIndex: -1,
          };

          const updatedContent = {
            ...currentPage.content,
            [repeaterSectionId]: {
              ...currentPage.content[repeaterSectionId],
              children: [
                ...(currentPage.content[repeaterSectionId].children || []),
                newParentWidgetId,
              ],
            },
            [newParentWidgetId]: addedWidget,
          };

          syncAddAcrossRepeaterContainers({
            addedWidget,
            currentPage: { ...currentPage, content: updatedContent },
            siteId: site.siteId,
            pageId: currentPage.pageId,
            dispatch,
          });

          return;
        }
      }

      addChild(widgetType, widgetConfig, 0, newParentWidgetId, 'root', -1);
      setLocData({
        x: xLoc,
        y: yLoc,
        originalYLocation: yLoc,
        widgetType: widgetType,
      });
      if (widgetType === widgetTypes.Tabs) {
        children = getInitialChildren(widgetTypes.Tabs);
        createChildWidgets(newParentWidgetId, children, e);
      }
      if (widgetType === widgetTypes.Card) {
        children = getInitialChildren(widgetTypes.Card);
        createChildWidgets(newParentWidgetId, children, e);
      }
      if (widgetType === widgetTypes.Form) {
        children = getInitialChildren(widgetTypes.Form, site);
        createChildWidgets(newParentWidgetId, children, e);
      }
      if (widgetType === widgetTypes.Banner) {
        props.updateWidgetPosition(newParentWidgetId, widgetConfig.height, 0);
        children = getInitialChildren(widgetTypes.Banner);
        createChildWidgets(newParentWidgetId, children, e, {
          isBannerWidgetChild: true,
        });
      }
      if (widgetType === widgetTypes.Slideshow) {
        children = getInitialChildren(widgetTypes.Slideshow);
        createChildWidgets(newParentWidgetId, children, e);
      }
      if (widgetType === widgetTypes.RepeaterWidget) {
        children = getInitialChildren(widgetTypes.RepeaterWidget);
        createChildWidgets(newParentWidgetId, children, e, {
          parentWidgetType: widgetTypes.RepeaterWidget,
          parentConfig: widgetConfig,
        });
      }
    }
  };

  const handleToggledNav = e => {
    if (config.navWidgetEnabled) {
      onDropWidget(e, widgetTypes.Navigation);
    }
  };

  const handleItemDescriptionValue = (widgetId: string) => {
    const currentItemDescription = widgetNames.find(
      widget => widget.id === widgetId
    )?.description;
    if (currentItemDescription === DEFAULT_CUSTOM_WIDGET_MESSAGE) {
      return 'Maximum of 120 characters.';
    }
    return currentItemDescription;
  };

  const WidgetsIcon = layoutLocked ? (
    <Tooltip
      title="This page has a locked layout, you cannot add widgets to the page"
      arrow
      placement="right-start"
      leaveDelay={100}
    >
      <IconBolt
        size={36}
        stroke={2}
        color="#999899"
        style={{ cursor: 'not-allowed' }}
      />
    </Tooltip>
  ) : (
    <IconBolt size={36} stroke={2} color="#2E3034" />
  );

  return (
    <StyledMenuItem
      icon={WidgetsIcon}
      onClick={() => toggleWidgetsPanelOpen(true)}
      rootStyles={{
        ['& > .' + menuClasses.button]: {
          color: defaultTheme.colors.aux.white,
          '&:hover': {
            backgroundColor: defaultTheme.colors.aux.white,
          },
          '&:focus': {
            backgroundColor: '#D9F6FA',
          },
        },
        ['.' + menuClasses.subMenuContent]: {
          backgroundColor: defaultTheme.colors.aux.white,
          width: isPanelOpen ? '264px' : '0px',
          margin: '-10px',
        },
        [`.${menuClasses.icon}`]: {
          width: '34px',
          minWidth: '34px',
          height: '34px',
          color: '#2E3034',
          '&:hover': {
            backgroundColor: defaultTheme.colors.aux.skyBlue,
            fill: defaultTheme.colors.aux.darkNavyBlue,
          },
        },
      }}
      title="Widgets"
      data-test-id="widgets-menu-icon"
      layoutLocked={!isEditingTemplate && props.currentPage.layoutLocked}
    >
      <div
        style={{ height: '775px', overflowY: 'scroll' }}
        onClick={handleInputClick}
      >
        <TopSection data-test-id="widgets-heading-section">
          <StyledHeading>Widgets</StyledHeading>
          <SharedIcon
            icon="Collapse"
            onClick={() => toggleWidgetsPanelOpen(!isPanelOpen)}
            width="22px"
            style={{ marginLeft: '114px' }}
          />
        </TopSection>
        <Group>
          <StyledPanel>
            <Panel
              data-test-id="widget-visual-elements-panel"
              title={<Label style={StyledPanelTitle}>Visual Elements</Label>}
              initiallyClosed
              icon={Arrow}
            >
              <IconGroupWrapper
                style={
                  !site.access.edit
                    ? { pointerEvents: 'none', opacity: '0.4' }
                    : {}
                }
              >
                <IconWrapper
                  draggable={true}
                  onDragEnd={e => onDropWidget(e, widgetTypes.Text)}
                  data-test-id="text-widget-icon"
                >
                  <IconLetterCase stroke={2} />
                  Text
                </IconWrapper>
                <IconWrapper
                  draggable={true}
                  onDragEnd={e => onDropWidget(e, widgetTypes.Image)}
                  id="image-widget-icon"
                  data-test-id="image-widget-icon"
                >
                  <IconPhoto stroke={2} />
                  Image
                </IconWrapper>
                <IconWrapper
                  draggable={true}
                  onDragEnd={e => onDropWidget(e, widgetTypes.VideoUpload)}
                >
                  <IconVideo stroke={2} />
                  VideoUpload
                </IconWrapper>
                <IconWrapper
                  draggable={true}
                  onDragEnd={e => onDropWidget(e, 'Button')}
                  data-test-id="button-widget-icon"
                >
                  <IconLayersLinked />
                  Button
                </IconWrapper>
                <IconWrapper
                  draggable={true}
                  onDragEnd={e => onDropWidget(e, widgetTypes.Link)}
                  data-test-id="link-widget-icon"
                >
                  <IconLink />
                  Link
                </IconWrapper>
                <IconWrapper
                  draggable={true}
                  onDragEnd={e => onDropWidget(e, widgetTypes.RichText)}
                  data-test-id="richtexteditor-widget-icon"
                >
                  <IconFileTextSpark />
                  Rich Text Editor
                </IconWrapper>
                <IconWrapper
                  draggable={true}
                  onDragEnd={e => onDropWidget(e, widgetTypes.Divider)}
                  data-test-id="divider-widget-icon"
                >
                  <IconSeparator />
                  Divider
                </IconWrapper>
                <IconWrapper
                  draggable={true}
                  onDragEnd={e => onDropWidget(e, widgetTypes.Html)}
                  data-test-id="html-widget-icon"
                >
                  <IconCode stroke={2} />
                  HTML
                </IconWrapper>
                <IconWrapper
                  draggable={true}
                  onDragEnd={e => onDropWidget(e, widgetTypes.Section)}
                  data-test-id="section-widget-icon"
                >
                  <IconSection />
                  Section
                </IconWrapper>

                <IconWrapper
                  draggable={true}
                  onDragEnd={e => onDropWidget(e, widgetTypes.UploadWidget)}
                  data-test-id="upload-widget-icon"
                >
                  <IconPhotoPlus />
                  Upload a widget
                </IconWrapper>
                <IconWrapper
                  draggable={true}
                  onDragEnd={e =>
                    onDropWidget(e, widgetTypes.NotificationWidget)
                  }
                  data-test-id="notification-widget-icon"
                >
                  <IconBell />
                  Notifications
                </IconWrapper>
              </IconGroupWrapper>
            </Panel>
          </StyledPanel>
          <StyledPanel>
            <Panel
              data-test-id="widget-components-panel"
              title={<Label style={StyledPanelTitle}>Components</Label>}
              initiallyClosed
              icon={Arrow}
            >
              <IconGroupWrapper
                style={
                  !site.access.edit
                    ? { pointerEvents: 'none', opacity: '0.4' }
                    : {}
                }
              >
                <IconWrapper
                  data-test-id="accordion-widget-icon-wrapper"
                  draggable={true}
                  onDragEnd={e => onDropWidget(e, widgetTypes.AccordionWidget)}
                >
                  <IconListDetails />
                  Accordion
                </IconWrapper>
                <IconWrapper
                  data-test-id="application-widget-icon"
                  draggable={true}
                  onDragEnd={e => onDropWidget(e, widgetTypes.Application)}
                >
                  <IconApps />
                  Application
                </IconWrapper>
                <IconWrapper
                  data-test-id="card-widget-icon"
                  draggable={true}
                  onDragEnd={e => onDropWidget(e, widgetTypes.Card)}
                >
                  <IconCards />
                  Card
                </IconWrapper>
                <IconWrapper
                  data-test-id="slideshow-widget-icon"
                  draggable={true}
                  onDragEnd={e => onDropWidget(e, widgetTypes.Slideshow)}
                >
                  <IconSlideshow />
                  Slideshow
                </IconWrapper>
                <IconWrapper
                  draggable={true}
                  onDragEnd={e => onDropWidget(e, widgetTypes.Banner)}
                  data-test-id="banner-widget-icon"
                >
                  <IconFlagSpark />
                  Banner
                </IconWrapper>
                <IconWrapper
                  data-test-id="tabs-widget-icon"
                  draggable={true}
                  onDragEnd={e => onDropWidget(e, widgetTypes.Tabs)}
                >
                  <IconNotebook />
                  Tabs
                </IconWrapper>
                <IconWrapper
                  draggable={true}
                  onDragEnd={e => handleToggledNav(e)}
                  data-test-id="navbar-widget-icon"
                >
                  <IconMap2 />
                  Navbar
                </IconWrapper>
                <IconWrapper
                  draggable={true}
                  onDragEnd={e => onDropWidget(e, widgetTypes.CalendarWidget)}
                  data-test-id="calendar-widget-icon"
                >
                  <IconCalendarMonth />
                  Calendar
                </IconWrapper>
                <IconWrapper
                  draggable={true}
                  onDragEnd={e => onDropWidget(e, widgetTypes.Search)}
                  data-test-id="search-widget-icon"
                >
                  <IconSearch />
                  Search
                </IconWrapper>
                <IconWrapper
                  draggable={true}
                  onDragEnd={e => onDropWidget(e, widgetTypes.RepeaterWidget)}
                  data-test-id="repeater-widget-icon"
                >
                  <IconSection />
                  Repeater
                </IconWrapper>
              </IconGroupWrapper>
            </Panel>
          </StyledPanel>
          <StyledPanel>
            <Panel
              data-test-id="widget-forms-panel"
              title={<Label style={StyledPanelTitle}>Forms</Label>}
              initiallyClosed
              icon={Arrow}
            >
              <IconGroupWrapper
                style={
                  !site.access.edit
                    ? { pointerEvents: 'none', opacity: '0.4' }
                    : {}
                }
              >
                <IconWrapper
                  draggable={true}
                  onDragEnd={e => onDropWidget(e, widgetTypes.Form)}
                  data-test-id="form-block-widget-icon"
                >
                  <IconTextCaption />
                  Form Block
                </IconWrapper>
                <IconWrapper
                  draggable={true}
                  onDragEnd={e => onDropWidget(e, widgetTypes.TextInput)}
                  data-test-id="text-input-widget-icon"
                >
                  <IconForms />
                  Text Input
                </IconWrapper>
                <IconWrapper
                  draggable={true}
                  onDragEnd={e => onDropWidget(e, widgetTypes.Checkbox)}
                  data-test-id="checkbox-widget-icon"
                >
                  <IconCheckbox />
                  Checkbox
                </IconWrapper>
                <IconWrapper
                  draggable={true}
                  onDragEnd={e => onDropWidget(e, widgetTypes.RadioButton)}
                  data-test-id="radio-widget-icon"
                >
                  <IconGradienter />
                  Radio
                </IconWrapper>
                <IconWrapper
                  draggable={true}
                  onDragEnd={e => onDropWidget(e, widgetTypes.Dropdown)}
                  data-test-id="dropdown-widget-icon"
                >
                  <IconSelect />
                  Dropdown
                </IconWrapper>
                <IconWrapper
                  draggable={true}
                  onDragEnd={e => onDropWidget(e, widgetTypes.FileUploader)}
                  data-test-id="file-uploader-widget-icon"
                >
                  <IconFileUpload />
                  File Uploader
                </IconWrapper>
                {/* <IconWrapper
                  draggable={true}
                  onDragEnd={e => onDropWidget(e, widgetTypes.RepeaterWidget)}
                  data-test-id="repeater-widget-icon"
                >
                  <IconSection />
                  Repeater
                </IconWrapper> */}
              </IconGroupWrapper>
            </Panel>
          </StyledPanel>
          <StyledPanel>
            <Panel
              data-test-id="my-widgets"
              title={
                <Label style={StyledPanelTitle}>
                  My Widgets ({parentWidgets.length})
                </Label>
              }
              initiallyClosed
              icon={Arrow}
            >
              {parentWidgets.length === 0 && (
                <StyledWidgetMessage>
                  You have no custom widgets saved. Create one by right-clicking
                  on an existing widget and selecting "Save to My Widgets".
                </StyledWidgetMessage>
              )}
              {parentWidgets.map(item => {
                return (
                  <IconWrapper
                    key={item}
                    draggable={true}
                    onDragEnd={e =>
                      onDropWidget(
                        e,
                        props.site.customWidgets.find(
                          widget => widget.widgetId === item
                        )?.widgetConfig.type,
                        props.site.customWidgets.find(
                          widget => widget.widgetId === item
                        )?.widgetConfig
                      )
                    }
                  >
                    <CustomWidgetPanelDiv>
                      <Label
                        style={{
                          color: 'black',
                          fontSize: '13.5px',
                          marginLeft: '20px',
                        }}
                      >
                        {widgetNames.find(widget => widget.id === item)?.name}
                      </Label>
                      <ListItemIcon
                        data-test-id="settings-list-icon"
                        className="settings"
                        style={{ paddingLeft: '33px' }}
                      >
                        <SharedIcon
                          data-test-id="settings-icon"
                          icon="MoreVertical"
                          height={18}
                          width={15}
                          onClick={e => handleSettingsClick(e, item)}
                        ></SharedIcon>
                      </ListItemIcon>
                      <div
                        style={{
                          fontSize: '12px',
                          paddingLeft: '9px',
                          paddingRight: '12px',
                          marginTop: '5px',
                          color: 'gray',
                        }}
                      >
                        {
                          widgetNames.find(widget => widget.id === item)
                            ?.description
                        }
                      </div>
                      <StyledPopover
                        id={widgetsId}
                        open={widgetOpen}
                        anchorEl={settingsAnchorEl}
                        onClose={handleClose}
                        anchorReference="anchorEl"
                        anchorPosition={{ top: 0, left: 350 }}
                        anchorOrigin={{
                          vertical: 'center',
                          horizontal: 'right',
                        }}
                        transformOrigin={{
                          vertical: 'top',
                          horizontal: 'left',
                        }}
                        data-test-id="my-widgets-popover"
                        PaperProps={{
                          style: {
                            left: '350px !important',
                          },
                        }}
                      >
                        {!openEditWidget && (
                          <StyledPage
                            style={StyledNewPage}
                            onClick={handleInputClick}
                            data-test-id="settings-page"
                          >
                            <StyledSettingsCard data-test-id="advanced-settings-card">
                              <StyledSettingName
                                data-test-id="edit-page-panel"
                                onClick={() => {
                                  setOpenEditWidgetFlag(true);
                                }}
                              >
                                <SharedIcon
                                  icon="Edit"
                                  width={15}
                                  style={iconStyle}
                                ></SharedIcon>
                                <StyledTextName>Edit Widget</StyledTextName>
                              </StyledSettingName>
                              <StyledSettingName
                                data-test-id="delete-page"
                                onClick={() => {
                                  handleDeleteClick(activeId);
                                }}
                              >
                                <SharedIcon
                                  icon="Delete"
                                  width={15}
                                  style={iconStyle}
                                ></SharedIcon>
                                <StyledTextName>
                                  Remove from My Widgets
                                </StyledTextName>
                              </StyledSettingName>
                            </StyledSettingsCard>
                          </StyledPage>
                        )}
                        {openEditWidget && (
                          <StyledPage
                            style={StyledNewPage}
                            onClick={handleInputClick}
                          >
                            <StyledCard
                              onClick={handleInputClick}
                              data-test-id="advanced-styledcard"
                            >
                              <Card
                                header={
                                  <Label style={StyledTitle}>Edit Widget</Label>
                                }
                                data-test-id="advanced-card"
                              >
                                <>
                                  <div
                                    style={{
                                      display: 'flex',
                                      marginTop: '20px',
                                    }}
                                  >
                                    <Label
                                      style={{
                                        color: 'black',
                                        fontSize: '13px',
                                        fontFamily: 'OptumSans',
                                        fontWeight: 'normal',
                                      }}
                                    >
                                      Name
                                    </Label>
                                    <StyledTextInput
                                      value={
                                        widgetNames.find(
                                          widget => widget.id === activeId
                                        )?.name
                                      }
                                      onChange={e => {
                                        handleItemNameChange(e, activeId);
                                      }}
                                      data-test-id="widget-name-input"
                                      style={{
                                        marginLeft: '30px',
                                        padding: '0.3rem',
                                        marginTop: '-1px',
                                      }}
                                    />
                                  </div>
                                  <Label
                                    style={{
                                      color: 'black',
                                      fontSize: '13px',
                                      fontFamily: 'OptumSans',
                                      fontWeight: 'normal',
                                      marginTop: '25px',
                                    }}
                                  >
                                    Description
                                  </Label>
                                  <TextArea
                                    value={handleItemDescriptionValue(activeId)}
                                    onChange={e => {
                                      handleItemDescriptionChange(e, activeId);
                                    }}
                                    data-test-id="widget-name-input"
                                    style={{
                                      padding: '0.3rem',
                                      marginTop: '5px',
                                      height: '100px',
                                      whiteSpace: 'no-wrap',
                                      width: '100%',
                                      fontFamily: 'OptumSans',
                                      fontSize: '14px',
                                      display: 'flex',
                                      color: 'black',
                                    }}
                                  />
                                  <ButtonWrapper>
                                    <Button
                                      onPress={handleSaveCustomWidgets}
                                      data-test-id="advanced-save-button"
                                    >
                                      Save
                                    </Button>
                                    <Button
                                      variant="tertiary"
                                      onClick={() => {
                                        setOpenEditWidgetFlag(false);
                                        reset();
                                      }}
                                      block
                                      className={'mt-base'}
                                      data-test-id="advanced-popover-cancel-button"
                                    >
                                      Cancel
                                    </Button>
                                  </ButtonWrapper>
                                </>
                              </Card>
                            </StyledCard>
                          </StyledPage>
                        )}
                      </StyledPopover>
                    </CustomWidgetPanelDiv>
                  </IconWrapper>
                );
              })}
            </Panel>
          </StyledPanel>
        </Group>
      </div>
    </StyledMenuItem>
  );
};
